import React from "react";
import { FiCheckCircle, FiClock, FiCpu, FiRepeat, FiShield, FiTrendingUp, FiZap, FiCode, FiServer, FiActivity } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuCpu,
  LuClipboardList,
  LuCode,
  LuBug,
  LuRocket,
  LuSearch,
  LuPalette,
  LuCircleCheck,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const automatedTestingHeroData = {
  backgroundImage: "/images/serviceBanners/automatedRegressionTesting.webp",
  whiteImage: "/images/serviceBanners/RegressionTestingBannerW.webp",
  subTitle: "We Create Automated Testing Solutions",
  title: (
    <>
      Deliver Quality with <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Automated Regression Testing Solutions</span>
    </>
  ),
  description:
    "Automated regression testing solutions designed to ensure software stability, detect issues early, and maintain consistent application performance while enabling faster, reliable, and high quality release cycles for businesses.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Testing Start clicked"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("Testing Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Fast and optimized automated testing execution.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Responsive Design",
      desc: "Works smoothly across all devices and screen sizes.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable",
      desc: "Ensures stable and secure regression testing pipelines.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clean Code",
      desc: "Maintainable and scalable automated test scripts.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

// 2. Stats Block
export const automatedTestingStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const automatedTestingSolutionsAndProcessData = {
  buildSubtitle: "OUR QA CAPABILITIES",
  buildTitle: "Automated Testing Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform Testing Support",
      desc: "Run automated tests across Android and iOS using scalable frameworks.",
      icon: FiZap,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise Testing Solutions",
      desc: "Ensure stable and secure application performance for large systems.",
      icon: FiServer,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Testing",
      desc: "Validate payment flows, carts, and user journeys with automation.",
      icon: FiCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand App Testing",
      desc: "Test real-time booking and service workflows with accuracy.",
      icon: FiActivity,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Testing Solutions",
      desc: "Ensure secure and compliant testing for complex financial applications.",
      icon: FiServer,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom QA Automation",
      desc: "Build tailored automated regression testing frameworks for explicit business needs.",
      icon: FiZap,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "AUTOMATED REGRESSION TESTING PROCESS",
  processTitle: "Automated Regression Testing Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand project requirements, testing scope, and automation goals.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define test strategy, tools, and regression automation roadmap.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Test Design",
      description: "Create reusable test cases and automation scripts for maximum code coverage.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "Test Development",
      description: "Build automated regression suites for stable application validation architectures.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Quality Testing",
      description: "Execute test cycles to ensure core functionality, deep security, and performance.",
      icon: LuCircleCheck, // Swapped with compile-safe icon to maintain clean deployment pipelines
    },
    {
      step: "06",
      title: "Deployment & Maintenance",
      description: "Integrate CI/CD pipelines and maintain automated test frameworks systematically.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR AUTOMATED REGRESSION TESTING SERVICES?",
  benefitsTitle: "Why Businesses Choose Automated Regression Testing Solutions",
  benefits: [
    "Cross-platform support",
    "Faster automation cycles",
    "Modern UI validation",
    "Smooth performance testing",
    "Efficient workflow system",
    "Scalable test solutions",
    "Cost-effective automation",
  ],
  logoImage: "/images/serviceBanners/automatedRegressionTesting.webp",
  whiteLogoImage: "/images/serviceBanners/RegressionTestingBannerW.webp",
};

// 4. Portfolio Data
export const automatedTestingPortfolioData = {
  subtitle: "CASE STUDIES",
  title: "Zero-Defect Delivery for Enterprise",
  buttonText: "View QA Projects",
  buttonLink: "#",
  apps: [
    {
      title: "Global FinTech Suite",
      badge: "Automation",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "A comprehensive regression suite of 2,000+ tests reducing release time from days to hours.",
      downloads: "Active",
      rating: "5.0",
      image: "/images/AI_ML_Services/automation1.jpg",
    },
  ],
};

// 5. Technologies Data
export const automatedTestingTechnologiesData = {
  techTitle: "OUR QA TOOL STACK",
  technologies: [
    { name: "Selenium", icon: "Code" },
    { name: "Cypress", icon: "Vite" },
    { name: "Playwright", icon: "RestAPI" },
    { name: "JMeter", icon: "Firebase" },
    { name: "Postman", icon: "ReactNative" },
    { name: "Appium", icon: "Dart" },
  ],
  trustTitle: "TRUSTED BY QUALITY-FIRST BRANDS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

// 6. About Overview
export const automatedTestingAboutData = [
  {
    title: "Automated Regression Testing Services for Reliable Software Quality",
    content: (
      <>
        Modern applications require consistent performance and stable functionality as updates and new features are released. Reliable{" "}
        <b className="text-text-primary font-semibold">automated regression testing services</b> help businesses reduce software risks, improve release confidence, and maintain
        application quality over time. Many organizations struggle with repetitive manual testing that slows product delivery and increases the chance of missed issues. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, automated testing frameworks are developed around business objectives, software reliability, and
        long-term quality assurance to help organizations maintain dependable digital systems.
      </>
    ),
  },
  {
    title: "Software Automation Testing Company for Better Product Stability",
    content: (
      <>
        Delivering high-quality software requires testing systems that can quickly identify issues before they impact users. A trusted{" "}
        <b className="text-text-primary font-semibold">software automation testing company</b> helps businesses improve software reliability by validating workflows, features, and
        user experiences across environments. Companies investing in <b className="text-text-primary font-semibold">qa automation services</b> often improve operational efficiency
        because automated frameworks reduce repetitive effort and improve testing accuracy. Tycho Technologies focuses on practical automation strategies designed to support faster
        software validation while maintaining stable application performance.
      </>
    ),
  },
  {
    title: "Selenium Testing Services for Smarter Quality Assurance",
    content: (
      <>
        Businesses managing complex applications often require testing systems that can handle continuous updates without affecting software reliability. Reliable{" "}
        <b className="text-text-primary font-semibold">selenium testing services</b> help organizations improve testing consistency, reduce human error, and support faster
        deployment cycles. Companies aiming to strengthen software quality frequently combine automation frameworks with{" "}
        <a href="https://tychotechnologies.com/security-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          security testing services
        </a>{" "}
        to identify vulnerabilities and improve digital trust before production releases.
      </>
    ),
  },
  {
    title: "Why Businesses Choose an Automation Testing Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">automation testing company in india</b> continues to grow as businesses seek scalable testing
        frameworks for modern software ecosystems. Companies investing in <b className="text-text-primary font-semibold">regression testing services india</b> often prioritize
        stability, faster release cycles, and better product performance. Businesses partnering with Tycho Technologies gain access to customized automation frameworks designed
        around measurable quality goals and business requirements. Many organizations also strengthen software reliability through{" "}
        <a href="https://tychotechnologies.com/testing-and-maintenance-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          software testing services
        </a>{" "}
        to support long-term digital performance and user satisfaction.
      </>
    ),
  },
];
// 7. FAQs
export const automatedTestingFaqs = [
  {
    q: "Why should I choose Automated QA services in India?",
    a: "Automated QA in India offers access to skilled professionals and cost-effective solutions with faster testing cycles for reliable software delivery.",
  },
  {
    q: "How do you ensure test reliability?",
    a: "We follow best practices for script design, use stable selectors, and maintain tests regularly to minimize flakiness.",
  },
];
